import { IDockviewPanelProps } from "dockview-react";
import React from "react";
import { api } from "./utils/api";

interface CourseItem {
  id: string;
  external_id: string;
  name: string;
  status: string;
  status_label: string;
  detail: string;
  schedule: string;
  location: string;
  date_range: string;
  capacity: number | null;
  prices: string;
  booking_window: string;
  target_open_at: string | null;
  raw_course: object;
}

interface SlotItem {
  product_id: string;
  product_name: string;
  start: string;
  end: string;
  start_label: string;
  end_label: string;
  status_label: string;
  is_takeable: boolean;
  booking_period_start: string | null;
  booking_period_start_label: string;
}

interface SlotResource {
  id: string;
  name: string;
  waiting_list_type: string;
}

interface SearchResult {
  kind: "course" | "slot";
  base_url: string;
  offer_url: string;
  name: string;
  items: (CourseItem | SlotItem)[];
  resources?: SlotResource[];
}

interface TrackedItem {
  id: number;
  kind: string;
  mode: string;
  state: string;
  offer_url: string;
  name: string;
  course_id: string | null;
  product_id: string | null;
  target_open_at_label: string;
  error: string | null;
  created_at_label: string;
  last_checked_at_label: string;
  resolved_at_label: string;
}

interface Watch {
  id: number;
  offer_url: string;
  name: string;
  known_course_ids: string[];
  active: boolean;
  created_at_label: string;
  last_checked_at_label: string;
}

const BOOKABLE_STATUSES = ["add_to_cart", "open"];

const STORAGE_KEYS = {
  url: "zhs_booking_url",
  rangeStart: "zhs_booking_range_start",
  rangeEnd: "zhs_booking_range_end",
  watchUrl: "zhs_booking_watch_url",
};

function loadStored(key: string): string {
  try {
    return localStorage.getItem(key) || "";
  } catch {
    return "";
  }
}

function saveStored(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    // ignore (private browsing / storage disabled)
  }
}

function isCourse(item: CourseItem | SlotItem): item is CourseItem {
  return (item as CourseItem).id !== undefined;
}

function slotKey(item: SlotItem): string {
  return `${item.product_id}|${item.start}|${item.end}`;
}

export const ZhsBooking = (_props: IDockviewPanelProps) => {
  const [url, setUrl] = React.useState(() => loadStored(STORAGE_KEYS.url));
  const [rangeStart, setRangeStart] = React.useState(() => loadStored(STORAGE_KEYS.rangeStart));
  const [rangeEnd, setRangeEnd] = React.useState(() => loadStored(STORAGE_KEYS.rangeEnd));
  const [searching, setSearching] = React.useState(false);
  const [searchError, setSearchError] = React.useState("");
  const [result, setResult] = React.useState<SearchResult | null>(null);
  const [selected, setSelected] = React.useState<Set<string>>(new Set());
  const [actionMessage, setActionMessage] = React.useState("");

  const [tracked, setTracked] = React.useState<TrackedItem[]>([]);
  const [watches, setWatches] = React.useState<Watch[]>([]);
  const [watchUrl, setWatchUrl] = React.useState(() => loadStored(STORAGE_KEYS.watchUrl));
  const [watchError, setWatchError] = React.useState("");

  React.useEffect(() => saveStored(STORAGE_KEYS.url, url), [url]);
  React.useEffect(() => saveStored(STORAGE_KEYS.rangeStart, rangeStart), [rangeStart]);
  React.useEffect(() => saveStored(STORAGE_KEYS.rangeEnd, rangeEnd), [rangeEnd]);
  React.useEffect(() => saveStored(STORAGE_KEYS.watchUrl, watchUrl), [watchUrl]);

  const fetchTracked = React.useCallback(() => {
    api
      .get("/zhs/tracked")
      .then((r) => setTracked(r.data))
      .catch((e) => console.error("Error fetching tracked items:", e));
  }, []);

  const fetchWatches = React.useCallback(() => {
    api
      .get("/zhs/new-release-watches")
      .then((r) => setWatches(r.data))
      .catch((e) => console.error("Error fetching new-release watches:", e));
  }, []);

  React.useEffect(() => {
    fetchTracked();
    fetchWatches();
    const interval = setInterval(() => {
      fetchTracked();
      fetchWatches();
    }, 5000);
    return () => clearInterval(interval);
  }, [fetchTracked, fetchWatches]);

  function handleSearch(e: React.FormEvent) {
    e.preventDefault();
    if (!url.trim()) return;

    setSearching(true);
    setSearchError("");
    setResult(null);
    setSelected(new Set());

    api
      .post("/zhs/search", {
        url: url.trim(),
        range_start: rangeStart ? rangeStart : undefined,
        range_end: rangeEnd ? rangeEnd : undefined,
      })
      .then((r) => setResult(r.data))
      .catch((e) => setSearchError(e?.response?.data?.detail || "Search failed."))
      .finally(() => setSearching(false));
  }

  function toggleSelected(key: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function handleBookNow(item: CourseItem | SlotItem) {
    if (!result) return;
    const body = isCourse(item)
      ? { kind: "course", base_url: result.base_url, offer_url: result.offer_url, course_id: item.id }
      : {
          kind: "slot",
          base_url: result.base_url,
          offer_url: result.offer_url,
          product_id: item.product_id,
          slot_start: item.start,
          slot_end: item.end,
        };

    setActionMessage("Booking...");
    api
      .post("/zhs/book-now", body)
      .then((r) => setActionMessage(r.data.message || "Added to cart."))
      .catch((e) => setActionMessage(e?.response?.data?.message || e?.response?.data?.detail || "Booking failed."));
  }

  function handleTrackSelected() {
    if (!result) return;
    const items = result.items.filter((item) =>
      selected.has(isCourse(item) ? item.id : slotKey(item))
    );

    setActionMessage(`Tracking ${items.length} item(s)...`);
    Promise.all(
      items.map((item) => {
        const body = isCourse(item)
          ? {
              kind: "course",
              base_url: result.base_url,
              offer_url: result.offer_url,
              name: item.name,
              status: item.status,
              course_id: item.id,
              target_open_at: item.target_open_at,
              raw_course: item.raw_course,
            }
          : {
              kind: "slot",
              base_url: result.base_url,
              offer_url: result.offer_url,
              name: `${item.product_name} ${item.start_label}-${item.end_label}`,
              product_id: item.product_id,
              slot_start: item.start,
              slot_end: item.end,
              target_open_at: item.booking_period_start,
            };
        return api.post("/zhs/track", body);
      })
    )
      .then(() => {
        setActionMessage(`Now tracking ${items.length} item(s).`);
        setSelected(new Set());
        fetchTracked();
      })
      .catch((e) => setActionMessage(e?.response?.data?.detail || "Tracking failed for one or more items."));
  }

  function handleDeleteTracked(id: number) {
    api
      .delete(`/zhs/tracked/${id}`)
      .then(() => fetchTracked())
      .catch((e) => console.error("Error deleting tracked item:", e));
  }

  function handleAddWatch(e: React.FormEvent) {
    e.preventDefault();
    if (!watchUrl.trim()) return;

    setWatchError("");
    api
      .post("/zhs/new-release-watches", { url: watchUrl.trim() })
      .then(() => {
        fetchWatches();
      })
      .catch((e) => setWatchError(e?.response?.data?.detail || "Could not start watch."));
  }

  function handleDeleteWatch(id: number) {
    api
      .delete(`/zhs/new-release-watches/${id}`)
      .then(() => fetchWatches())
      .catch((e) => console.error("Error deleting watch:", e));
  }

  return (
    <div className="p-4 overflow-auto h-full">
      <div className="flex items-center justify-between gap-2 mb-2">
        <h1>ZHS Booking</h1>
        <button
          className="btn btn-default"
          onClick={() => {
            fetchTracked();
            fetchWatches();
          }}
        >
          Refresh
        </button>
      </div>

      <form onSubmit={handleSearch} className="flex flex-row gap-2 items-center mb-2">
        <input
          type="text"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="Paste a ZHS course or product-offer URL"
          className="border rounded px-2 py-1 flex-1 min-w-0"
        />
        <input
          type="datetime-local"
          value={rangeStart}
          onChange={(e) => setRangeStart(e.target.value)}
          className="border rounded px-2 py-1 shrink-0"
        />
        <span>-</span>
        <input
          type="datetime-local"
          value={rangeEnd}
          onChange={(e) => setRangeEnd(e.target.value)}
          className="border rounded px-2 py-1 shrink-0"
        />
        <button type="submit" className="btn btn-primary shrink-0" disabled={searching}>
          {searching ? "Searching..." : "Search"}
        </button>
      </form>

      {searchError && <div className="text-red-500 mb-2">{searchError}</div>}
      {actionMessage && <div className="mb-2 opacity-80">{actionMessage}</div>}

      {result && (
        <div className="mb-4">
          <h2 className="font-bold">{result.name}</h2>

          {result.kind === "slot" && result.resources && result.items.length === 0 && (
            <div className="mb-2">
              <div className="opacity-70 mb-1">
                {result.resources.length} bookable resource(s) found. Fill in a time range above and search again to see slots.
              </div>
              {result.resources.map((res) => (
                <div key={res.id}>- {res.name}</div>
              ))}
            </div>
          )}

          {result.items.length > 0 && (
            <>
              <div className="flex flex-col gap-2">
                {result.items.map((item) => {
                  const key = isCourse(item) ? item.id : slotKey(item);
                  const label = isCourse(item)
                    ? item.name
                    : `${item.product_name} ${item.start_label} - ${item.end_label}`;
                  const statusLabel = item.status_label;
                  const bookable = isCourse(item) ? BOOKABLE_STATUSES.includes(item.status) : item.is_takeable;

                  return (
                    <div key={key} className="flex items-start justify-between gap-2 border-b pb-2">
                      <div className="flex items-start gap-2">
                        <input
                          type="checkbox"
                          checked={selected.has(key)}
                          onChange={() => toggleSelected(key)}
                          className="mt-1"
                        />
                        <div>
                          <div className="font-bold">{label}</div>
                          <div className="text-sm opacity-70">{statusLabel}</div>
                          {isCourse(item) && (
                            <div className="text-sm opacity-70">
                              {item.schedule} | {item.date_range} | {item.location} | {item.prices}
                            </div>
                          )}
                        </div>
                      </div>
                      {bookable && (
                        <button className="btn btn-success" onClick={() => handleBookNow(item)}>
                          Book now
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>

              {selected.size > 0 && (
                <button className="btn btn-primary mt-2" onClick={handleTrackSelected}>
                  Track {selected.size} selected item(s)
                </button>
              )}
            </>
          )}
        </div>
      )}

      <h1>Tracked items</h1>
      <div className="flex flex-col gap-2 mb-4">
        {tracked.length === 0 && <div className="opacity-70">Nothing being tracked.</div>}
        {tracked.map((item) => (
          <div key={item.id} className="flex items-center justify-between gap-2 border-b pb-2">
            <div>
              <div className="font-bold">{item.name}</div>
              <div className="text-sm opacity-70">
                {item.kind}/{item.mode} - {item.state}
                {item.mode === "burst" && item.target_open_at_label !== "-" && ` - opens ${item.target_open_at_label}`}
                {item.error && ` - ${item.error}`}
              </div>
              <div className="text-sm opacity-50">
                tracked since {item.created_at_label} | last checked {item.last_checked_at_label}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button className="btn btn-default" onClick={() => window.open(item.offer_url, "_blank")}>
                See in ZHS
              </button>
              <button className="btn btn-default" onClick={() => handleDeleteTracked(item.id)}>
                Remove
              </button>
            </div>
          </div>
        ))}
      </div>

      <h1>New releases notifier</h1>
      <form onSubmit={handleAddWatch} className="flex gap-2 mb-2">
        <input
          type="text"
          value={watchUrl}
          onChange={(e) => setWatchUrl(e.target.value)}
          placeholder="Paste a ZHS course offer URL to watch for new courses"
          className="border rounded px-2 py-1 flex-1"
        />
        <button type="submit" className="btn btn-primary">
          Watch
        </button>
      </form>
      {watchError && <div className="text-red-500 mb-2">{watchError}</div>}

      <div className="flex flex-col gap-2">
        {watches.length === 0 && <div className="opacity-70">No active watches.</div>}
        {watches.map((watch) => (
          <div key={watch.id} className="flex items-center justify-between gap-2 border-b pb-2">
            <div>
              <div className="font-bold">{watch.name}</div>
              <div className="text-sm opacity-70">
                {watch.known_course_ids.length} known course(s) | last checked {watch.last_checked_at_label}
              </div>
            </div>
            <button className="btn btn-default" onClick={() => handleDeleteWatch(watch.id)}>
              Stop
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ZhsBooking;
